import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Send, Users, Check, X, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const JoinRequests = ({ teamId, headers, onChange }) => {
  const [reqs, setReqs] = useState([]);
  const load = () => axios.get(`${API}/api/event-teams/teams/${teamId}/join-requests`, { headers }).then((r) => setReqs(r.data.requests || [])).catch(() => {});
  useEffect(() => { load(); }, [teamId]);
  const decide = async (id, d) => {
    try { const r = await axios.post(`${API}/api/event-teams/teams/${teamId}/join-requests/${id}/${d}`, {}, { headers }); toast.success(r.data.message); load(); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  if (!reqs.length) return null;
  return (
    <div className="space-y-2" data-testid="join-requests">
      <p className="text-xs font-bold text-[#A1A1AA] uppercase tracking-[0.1em]">Requests to join ({reqs.length})</p>
      {reqs.map((r) => (
        <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA]" data-testid={`join-request-${r.id}`}>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0A0A0A]">{r.name} <span className="text-xs font-normal text-[#A1A1AA]">{r.college}</span></p>
            {r.skills?.length > 0 && <p className="text-xs text-[#52525B]">{r.skills.join(' · ')}</p>}
            {r.message && <p className="text-xs text-[#52525B] italic truncate">“{r.message}”</p>}
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => decide(r.id, 'approve')} className="p-2 bg-emerald-600 text-white rounded" title="Approve" data-testid={`approve-join-${r.id}`}><Check size={14} /></button>
            <button onClick={() => decide(r.id, 'reject')} className="p-2 border border-[#E4E4E7] rounded text-[#52525B]" title="Decline" data-testid={`reject-join-${r.id}`}><X size={14} /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

const PostCard = ({ eventId, headers, mine, onDone }) => {
  const [skills, setSkills] = useState(mine?.skills?.join(', ') || '');
  const [role, setRole] = useState(mine?.role || '');
  const [bio, setBio] = useState(mine?.bio || '');
  const save = async () => {
    try {
      const r = await axios.post(`${API}/api/event-teams/${eventId}/finder`, { skills: skills.split(','), role, bio }, { headers });
      toast.success(r.data.message); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not post card'); }
  };
  const inp = 'w-full px-3 py-2 border border-[#E4E4E7] rounded text-sm text-[#0A0A0A] focus:outline-none focus:border-[#002FA7]';
  return (
    <div className="p-4 rounded-lg border border-dashed border-[#002FA7]/40 bg-[#002FA7]/5 space-y-2" data-testid="finder-post-card">
      <p className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-1.5"><Sparkles size={14} className="text-[#002FA7]" /> {mine ? 'Update your card' : 'Looking for a team? Post your card'}</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={skills} onChange={(e) => setSkills(e.target.value)} placeholder="Skills, comma separated (React, ML, UI)" className={inp} data-testid="finder-skills-input" />
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role you want (e.g. Frontend dev)" className={inp} data-testid="finder-role-input" />
      </div>
      <textarea rows={2} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Short intro" className={inp} data-testid="finder-bio-input" />
      <div className="flex gap-2 justify-end">
        {mine && <button onClick={() => axios.delete(`${API}/api/event-teams/${eventId}/finder`, { headers }).then(() => { toast.success('Card removed'); onDone(); })} className="px-3 py-2 text-xs text-[#52525B]" data-testid="finder-remove-card-btn">Remove card</button>}
        <button onClick={save} className="px-4 py-2 bg-[#002FA7] text-white rounded text-sm font-semibold" data-testid="finder-post-btn">{mine ? 'Update' : 'Post card'}</button>
      </div>
    </div>
  );
};

// Board of solo players + teams with open spots; leaders invite, players request to join
export const TeamFinderBoard = ({ eventId, team, isLeader, headers, onChange }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('players');
  const [invited, setInvited] = useState({});
  const load = () => axios.get(`${API}/api/event-teams/${eventId}/finder`, { headers }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [eventId, team?.id, team?.member_count]);
  const act = async (fn) => { try { const r = await fn(); toast.success(r.data.message); load(); onChange(); return true; } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); return false; } };
  if (!data) return null;
  const mine = data.seekers.find((s) => s.mine);
  const canInvite = isLeader && team?.status === 'forming';
  const others = data.seekers.filter((s) => !s.mine);
  return (
    <div className="pt-5 border-t border-[#E4E4E7] space-y-4" data-testid="team-finder-board">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-extrabold text-[#0A0A0A] flex items-center gap-2"><Users size={16} /> Team Finder</p>
        <div className="flex bg-[#F4F4F5] rounded p-0.5">
          {[['players', `Players (${others.length})`], ['teams', `Open teams (${data.open_teams.filter((t) => t.id !== team?.id).length})`]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`finder-tab-${v}`} className={`px-3 py-1 rounded text-xs font-semibold ${tab === v ? 'bg-white shadow-sm text-[#0A0A0A]' : 'text-[#71717A]'}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data.in_team && <PostCard eventId={eventId} headers={headers} mine={mine} onDone={load} />}
      {tab === 'players' ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {others.length === 0 && <p className="text-xs text-[#A1A1AA]">No players looking for a team yet.</p>}
          {others.map((s) => (
            <div key={s.user_id} className="p-3 rounded-lg border border-[#E4E4E7] bg-white" data-testid={`seeker-card-${s.user_id}`}>
              <p className="text-sm font-semibold text-[#0A0A0A]">{s.name} <span className="text-xs font-normal text-[#A1A1AA]">{s.college}</span></p>
              {s.role && <p className="text-xs text-[#002FA7] font-medium">{s.role}</p>}
              <div className="flex flex-wrap gap-1 mt-1.5">{s.skills.map((k) => <span key={k} className="px-1.5 py-0.5 bg-[#F4F4F5] rounded text-[10px] font-semibold text-[#52525B]">{k}</span>)}</div>
              {s.bio && <p className="text-xs text-[#52525B] mt-1.5 line-clamp-2">{s.bio}</p>}
              {canInvite && (invited[s.user_id]
                ? <span className="mt-2 inline-flex text-[11px] font-semibold text-emerald-700 uppercase" data-testid={`finder-invited-${s.user_id}`}>Invited</span>
                : <button onClick={() => act(() => axios.post(`${API}/api/event-teams/teams/${team.id}/invites`, { user_id: s.user_id }, { headers }))
                    .then((ok) => ok && setInvited((x) => ({ ...x, [s.user_id]: true })))}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#002FA7]" data-testid={`finder-invite-${s.user_id}`}><UserPlus size={13} /> Invite to {team.name}</button>)}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.open_teams.filter((t) => t.id !== team?.id).map((t) => (
            <div key={t.id} className="p-3 rounded-lg border border-[#E4E4E7] bg-white flex items-center justify-between gap-2" data-testid={`open-team-${t.id}`}>
              <div><p className="text-sm font-semibold text-[#0A0A0A]">{t.name}</p><p className="text-xs text-[#A1A1AA]">Led by {t.leader_name} · {t.spots_left} spot{t.spots_left === 1 ? '' : 's'} left</p></div>
              {!data.in_team && (t.requested
                ? <span className="text-[11px] font-semibold text-[#A1A1AA] uppercase">Requested</span>
                : <button onClick={() => act(() => axios.post(`${API}/api/event-teams/teams/${t.id}/join-requests`, { message: '' }, { headers }))}
                    className="inline-flex items-center gap-1 text-xs font-bold text-[#002FA7]" data-testid={`request-join-${t.id}`}><Send size={12} /> Request to join</button>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
